import { reviewQuestionKey } from "./semanticReviewModel";
import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

export interface AnswerEffect {
  id: string;
  question: string;
  answerText: string;
  wordings: string[];
  remainingQuestions: number;
}
export type AnswerChoice = "keep" | "separate" | "combine" | "mergeQuestions";
interface Props {
  question: string;
  questions: string[];
  currentAnswer: string;
  proposedAnswer: string;
  currentAvailable: boolean;
  anotherAvailable: boolean;
  multipleExisting: boolean;
  currentQuestion: string;
  equivalent: boolean;
  conflict: boolean;
  matchedQuestion?: string;
  reason?: string;
  combineBlocked?: string;
  mergeBlocked?: string;
  mergeQuestions: string[];
  existingQuestions: string[];
  existingSource: string;
  busy: boolean;
  version: string;
  combinedEffects: AnswerEffect[];
  proposedQuestions: string[];
  onChoose: (choice: AnswerChoice, questions?: string[]) => Promise<boolean>;
  onReviewCurrent: () => void;
}

export function SemanticAnswerComparison(props: Props) {
  const [confirmation, setConfirmation] = useState<{
    choice: Exclude<AnswerChoice, "separate">;
    version: string;
    questions: string[];
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);
  const choice = confirmation?.choice;
  const selectedKeys = new Set(
    [...props.existingQuestions, ...(confirmation?.questions || [])].map(
      reviewQuestionKey,
    ),
  );
  const effects = props.combinedEffects.flatMap((effect) => {
    const wordings = effect.wordings.filter((question) =>
      selectedKeys.has(reviewQuestionKey(question)),
    );
    return wordings.length
      ? [
          {
            ...effect,
            wordings,
            remainingQuestions:
              effect.remainingQuestions +
              effect.wordings.length -
              wordings.length,
          },
        ]
      : [];
  });
  const spokenAnswer =
    choice === "combine" || choice === "mergeQuestions"
      ? props.currentAnswer
      : props.proposedAnswer;
  const stale = !!confirmation && confirmation.version !== props.version;
  const blocked =
    choice === "mergeQuestions"
      ? props.mergeBlocked ||
        (!confirmation?.questions.length
          ? "Select at least one question."
          : undefined)
      : choice === "combine"
        ? props.combineBlocked ||
          (!confirmation?.questions.length
            ? "Select at least one question to add."
            : undefined)
        : undefined;
  const choose = (next: AnswerChoice) => {
    if (next === "separate") {
      void props.onChoose(next);
      return;
    }
    setConfirmation({
      choice: next,
      version: props.version,
      questions: [...props.mergeQuestions],
    });
  };
  const confirm = async () => {
    if (
      !choice ||
      stale ||
      blocked ||
      submitting ||
      props.busy ||
      submitLock.current
    )
      return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      if (await props.onChoose(choice, confirmation?.questions))
        setConfirmation(null);
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };
  return (
    <Box
      component="section"
      aria-label={`Answer choice for ${props.question}`}
      sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2, mb: 2 }}
    >
      <Typography variant="subtitle1" fontWeight={600}>
        {props.equivalent
          ? "This question already has an equivalent answer"
          : props.conflict
            ? props.matchedQuestion
              ? "Same question, different answers"
              : "These answers disagree"
            : props.anotherAvailable
              ? "Choose which answer to use"
              : "This question already has an answer"}
      </Typography>
      {props.matchedQuestion && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Matches the existing question phrasing: “{props.matchedQuestion}”
        </Typography>
      )}
      <Typography variant="body2" sx={{ mt: 1 }}>
        Choose how to handle the proposed question and answer:
      </Typography>
      {props.questions.map((q) => (
        <Typography key={q} variant="body2" sx={{ mt: 0.5 }}>
          “{q}”
        </Typography>
      ))}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
          my: 2,
        }}
      >
        <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
          <Typography variant="subtitle2">
            {props.currentAvailable
              ? props.anotherAvailable
                ? "Another available answer"
                : "Existing available answer"
              : "Existing answer — awaiting approval"}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            For: “{props.currentQuestion}”
          </Typography>
          <Typography
            sx={{ mt: 1, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
          >
            {props.currentAnswer}
          </Typography>
        </Box>
        <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
          <Typography variant="subtitle2">Answer you’re reviewing</Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            For: “{props.question}”
          </Typography>
          <Typography
            sx={{ mt: 1, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
          >
            {props.proposedAnswer}
          </Typography>
        </Box>
      </Box>
      {props.equivalent ? (
        <Typography variant="body2">
          Both answers convey the same verified information. Add the question
          examples to the existing answer.
        </Typography>
      ) : props.conflict ? (
        <Alert severity="warning">
          Verification found different facts or missing details between the
          existing answer and the answer you’re reviewing. Each question must
          have a supported answer before it can be reused.
        </Alert>
      ) : (
        <Typography variant="body2">
          If these questions ask for different details, make their examples more
          specific and check them again.
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
          mt: 2,
        }}
      >
        <Box>
          <Button
            variant="contained"
            disabled={
              props.busy ||
              submitting ||
              !!(props.equivalent ? props.combineBlocked : props.mergeBlocked)
            }
            onClick={() =>
              void choose(props.equivalent ? "combine" : "mergeQuestions")
            }
          >
            Keep existing answer and merge questions
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {(props.equivalent ? props.combineBlocked : props.mergeBlocked) ||
              "Add selected proposed questions to the existing question group, using its answer."}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            disabled={props.busy || submitting}
            onClick={() => void choose("separate")}
          >
            Keep both separately
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Keep each question with its own answer. Review and check the
            proposed pair before reuse.
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            color="error"
            disabled={props.busy || submitting}
            onClick={() => void choose("keep")}
          >
            Reject proposed question and answer
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Keep the existing pair. Remove the proposed pair from reuse and
            retain its history.
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            disabled={props.busy || submitting}
            onClick={props.onReviewCurrent}
          >
            Review existing question and answer
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Open the existing pair to inspect or edit it.
          </Typography>
        </Box>
      </Box>
      {props.equivalent && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Diagnosis will use the existing answer for the combined question
          examples. The collected exchange will remain in review history.
        </Typography>
      )}
      {props.reason && !props.conflict && (
        <Box component="details" sx={{ mt: 1 }}>
          <Box component="summary" sx={{ cursor: "pointer" }}>
            Why these answers are related
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {props.reason}
          </Typography>
        </Box>
      )}
      <Dialog
        open={!!confirmation}
        onClose={() => setConfirmation(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {choice === "mergeQuestions"
            ? "Keep this answer and check selected questions?"
            : choice === "keep"
              ? "Reject the proposed question and answer?"
              : "Combine these question examples?"}
        </DialogTitle>
        <DialogContent dividers>
          {stale && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The review changed. Cancel this choice and compare the updated
              answers before continuing.
            </Alert>
          )}
          {blocked && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {blocked}
            </Alert>
          )}
          <Typography variant="subtitle2">
            {choice === "keep" ? "Proposed answer to reject" : "Patient answer"}
          </Typography>
          <Typography sx={{ my: 1, whiteSpace: "pre-wrap" }}>
            {spokenAnswer}
          </Typography>
          {choice === "mergeQuestions" || choice === "combine" ? (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Select questions to add to the existing answer
              </Typography>
              <Stack>
                {props.existingQuestions.map((question) => (
                  <FormControlLabel
                    key={`existing:${question}`}
                    label={`${question} (already attached)`}
                    control={
                      <Checkbox
                        checked
                        disabled
                        inputProps={{ "aria-label": question }}
                        sx={{ "&.Mui-disabled": { color: "text.secondary" } }}
                      />
                    }
                  />
                ))}
                {props.mergeQuestions.map((question) => (
                  <FormControlLabel
                    key={question}
                    label={question}
                    control={
                      <Checkbox
                        checked={!!confirmation?.questions.includes(question)}
                        disabled={submitting || props.busy || stale}
                        onChange={(_, checked) =>
                          setConfirmation(
                            (current) =>
                              current && {
                                ...current,
                                questions: checked
                                  ? [...current.questions, question]
                                  : current.questions.filter(
                                      (q) => q !== question,
                                    ),
                              },
                          )
                        }
                      />
                    }
                  />
                ))}
              </Stack>
              <Alert severity="info" sx={{ mt: 2 }}>
                The existing questions and answer will stay the same. Unselected
                proposed questions will not be added.
                {choice === "combine"
                  ? " Selected questions have already passed verification against the existing answer. Approve and combine publishes only your selection and retains the collected exchange in review history."
                  : " Selected questions will be checked against this answer before reuse."}
                {choice === "mergeQuestions" &&
                  props.currentAvailable &&
                  " The published answer stays available while checking runs."}
                {choice === "mergeQuestions" &&
                  (props.existingSource === "session"
                    ? " Administrator approval is required after checking."
                    : " Questions that pass can become available automatically.")}
                {choice === "mergeQuestions" &&
                  " The answer you’re reviewing and its history will be retained; its status will not change."}
              </Alert>
              {choice === "combine" &&
                effects.map((effect) => (
                  <Alert key={effect.id} severity="warning" sx={{ mt: 1 }}>
                    Selected questions will use the existing answer instead of
                    the answer for “{effect.question}”:{" "}
                    {effect.wordings.join("; ")}.
                    {effect.remainingQuestions === 0
                      ? " That answer will have no remaining questions and will become unavailable; its history is retained."
                      : ` Its other ${effect.remainingQuestions} questions will be kept.`}
                  </Alert>
                ))}
            </>
          ) : (
            <>
              <Typography variant="subtitle2">
                {choice === "keep"
                  ? "Proposed questions to reject"
                  : "Diagnosis will use this answer for"}
              </Typography>
              {props.proposedQuestions.map((q) => (
                <Typography variant="body2" key={q} sx={{ mt: 0.5 }}>
                  “{q}”
                </Typography>
              ))}

              <Alert severity="info" sx={{ mt: 2 }}>
                The answer you’re reviewing will be rejected, including all of
                its question examples. Its text and history will be retained.
                The existing question and answer will stay unchanged.
                {props.multipleExisting &&
                  " Other existing answers also cover these questions. Review them to choose a single response."}
                {!props.currentAvailable &&
                  " The existing answer still needs approval before diagnosis can use it."}
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {submitting && (
            <Typography variant="body2" sx={{ mr: "auto" }}>
              Saving your choice… You can close this confirmation while it
              finishes.
            </Typography>
          )}
          <Button onClick={() => setConfirmation(null)}>
            {submitting ? "Close" : "Cancel"}
          </Button>
          <Button
            variant="contained"
            disabled={props.busy || submitting || stale || !!blocked}
            onClick={() => void confirm()}
          >
            {choice === "mergeQuestions"
              ? "Keep answer and check questions"
              : choice === "keep"
                ? "Reject proposed question and answer"
                : "Approve and combine"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
